import gql from 'graphql-tag';

export const GET_EMPLOYEE_BADGE_ASSIGNMENTS = gql`
	query QueryBadgeAssignments($companyId: ID!, $userId: ID, $limit: Int) {
		queryBadgeAssignments(
      		companyId: $companyId
			userId: $userId
      		limit: $limit
    	) {
			items {
				approvalStatus
				awardedBy
				id
				badgeId
				badge {
					id
					name
					snappyGrantId
					snappyGrant {
						id
						name
						externalSnappyId
						snappyCampaignTierId
						costInCurrency
					}
				}
				rewardBonus
				companyId
				userGroupId
				userId
				createdAt
				updatedAt
				user {
				id
				firstName
				lastName
				emailAddress
				}
			}
      		nextToken
		}
	}
`;

export const CREATE_SNAPPY_GIFT = gql`
	mutation CreateSnappyGift($input: SnappyGiftInput!) {
		createSnappyGift(input: $input) {
			id
			companyId
			userId
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			link
			claimedOn
		}
	}
`;

export const UPDATE_SNAPPY_GIFT = gql`
	mutation UpdateSnappyGift($input: SnappyGiftInput!) {
		updateSnappyGift(input: $input) {
			id
			companyId
			userId
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			link
			claimedOn
		}
	}
`;

export const QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID = gql`
	query QuerySnappyGiftsByUserIdCompanyId($userId: ID!, $companyId: ID!) {
		querySnappyGiftsByUserIdCompanyId(userId: $userId, companyId: $companyId) {
			id
			companyId
			itemType
			userId
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			firstSeenAt
			createdAt
			claimedOn
		}
	}
`;

export class SnappyGiftService {
	constructor(apolloClient) {
		this.apolloClient = apolloClient;
	}

	async getEmployeeBadgeAssignments(companyId, userId, limit = 20) {
		try {
			const { data } = await this.apolloClient.query({
				query: GET_EMPLOYEE_BADGE_ASSIGNMENTS,
				variables: { companyId, userId, limit },
				fetchPolicy: 'network-only'
			});
			return data?.queryBadgeAssignments?.items || [];
		} catch (error) {
			console.error('Error fetching employee badge assignments:', error);
			return [];
		}
	}


	async createGiftsForUser(userId, campaignTierId) {
		try {
			const { snappy } = await import('src/_shared');
			const response = await snappy({
				type: 'createGifts',
				userId: userId,
				sendingMethod: 'link',
				recipients: [userId],
				campaignTierId: campaignTierId,
			});

			if (response && response.length > 0) {
				const result = response[0];
				if (result.giftId && result.giftDetails && result.giftDetails.link) {
					return result;
				} else {
					console.error('Error creating gift:', result.errorMessage);
					throw new Error(result.errorMessage || 'Failed to create gift');
				}
			} else {
				throw new Error('Unexpected response from createGifts');
			}
		} catch (error) {
			console.error('Error creating snappy gift:', error);
			throw error;
		}
	}

	async createSnappyGiftRecord(input) {
		try {
			const { data } = await this.apolloClient.mutate({
				mutation: CREATE_SNAPPY_GIFT,
				variables: { input }
			});
			return data?.createSnappyGift;
		} catch (error) {
			console.error('Error creating snappy gift record:', error);
			throw error;
		}
	}

	async updateSnappyGift(input) {
		try {
			const { data } = await this.apolloClient.mutate({
				mutation: UPDATE_SNAPPY_GIFT,
				variables: { input }
			});
			return data?.updateSnappyGift;
		} catch (error) {
			console.error('Error updating snappy gift:', error);
			throw error;
		}
	}

	async querySnappyGiftsByUserIdCompanyId(userId, companyId) {
		try {
			const { data } = await this.apolloClient.query({
				query: QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID,
				variables: { userId, companyId },
				fetchPolicy: 'network-only'
			});
			return data?.querySnappyGiftsByUserIdCompanyId || [];
		} catch (error) {
			console.error('Error querying snappy gifts by user and company:', error);
			return [];
		}
	}

	async ensureSnappyGiftExists(userId, companyId, snappyGrantId) {
		try {
			const existingGifts = await this.querySnappyGiftsByUserIdCompanyId(userId, companyId);
			const existingGift = existingGifts.find(gift => gift.snappyGrantId === snappyGrantId);
			
			if (existingGift) {
				return existingGift;
			}

			const now = new Date().toISOString();
			const newGiftInput = {
				userId,
				companyId,
				snappyGrantId,
				externalSnappyGiftId: "undefined"
			};

			return await this.createSnappyGiftRecord(newGiftInput);
		} catch (error) {
			console.error('Error ensuring snappy gift exists:', error);
			throw error;
		}
	}

	async markGiftAsSeen(userId, companyId, snappyGrantId) {
		try {
			const gift = await this.ensureSnappyGiftExists(userId, companyId, snappyGrantId);
			
			if (gift.firstSeenAt) {
				return gift;
			}

			const updateInput = {
				id: gift.id,
				userId: userId,
				companyId: companyId,
				snappyGrantId: snappyGrantId
			};

			return await this.updateSnappyGift(updateInput);
		} catch (error) {
			console.error('Error marking gift as seen:', error);
			throw error;
		}
	}

	async processGiftClaim(userId, companyId, snappyGrantId, campaignTierId) {
		try {
			const gift = await this.markGiftAsSeen(userId, companyId, snappyGrantId);
			
			if (gift.externalSnappyGiftId) {
				return gift;
			}

			try {
				const snappyResult = await this.createGiftsForUser(userId, campaignTierId);
				
				if (snappyResult && snappyResult.giftId) {
					// Success - update the gift record with external ID and claim date
					const updateInput = {
						id: gift.id,
						externalSnappyGiftId: snappyResult.giftId,
						userId: userId,
						companyId: companyId,
						snappyGrantId: snappyGrantId,
						snappyCampaignTierId: campaignTierId,
						snappyCampaignId: gift.snappyCampaignId,
						link: snappyResult.giftDetails.link,
						claimedOn: new Date().toISOString()
					};
					
					const updatedGift = await this.updateSnappyGift(updateInput);
					
					// If the gift has a link, open it
					if (snappyResult.giftDetails?.link) {
						window.open(snappyResult.giftDetails.link, '_blank');
					}
					
					return updatedGift;
				} else {
					throw new Error('No gift ID returned from Snappy API');
				}
			} catch (snappyError) {
				console.error('Snappy API call failed:', snappyError);
				
				// The gift record already has firstSeenAt set, so it won't trigger spotlight again
				throw new Error('Failed to create gift through Snappy API. Please try again later.');
			}
		} catch (error) {
			console.error('Error processing gift claim:', error);
			throw error;
		}
	}

	async checkForNewGifts(userId, companyId, badgeAssignments) {
		try {
			const newGifts = [];
			const firstTimeGifts = [];
			const subsequentGifts = [];
			
			for (const assignment of badgeAssignments) {
				if (assignment.approvalStatus === 'Approved' && assignment.badge?.snappyGrantId) {
					const existingGifts = await this.querySnappyGiftsByUserIdCompanyId(userId, companyId);
					const existingGift = existingGifts.find(gift => gift.snappyGrantId === assignment.badge.snappyGrantId);
					
					if (!existingGift || !existingGift.externalSnappyGiftId) {
						const giftInfo = {
							snappyGrantId: assignment.badge.snappyGrantId,
							badgeName: assignment.badge.name,
							campaignTierId: assignment.badge.snappyGrant?.id,
							isFirstTime: !existingGift,
							hasBeenSeen: existingGift?.firstSeenAt !== null,
							assignment
						};
						
						if (!existingGift) {
							await this.ensureSnappyGiftExists(userId, companyId, assignment.badge.snappyGrantId);
							firstTimeGifts.push(giftInfo);
						} else if (!existingGift.firstSeenAt) {
							// Gift exists but hasn't been seen yet - still first time
							firstTimeGifts.push(giftInfo);
						} else {
							// Gift has been seen but not claimed - subsequent view
							subsequentGifts.push(giftInfo);
						}
						
						newGifts.push(giftInfo);
					}
				}
			}
			
			return {
				allGifts: newGifts,
				firstTimeGifts,
				subsequentGifts
			};
		} catch (error) {
			console.error('Error checking for new gifts:', error);
			return {
				allGifts: [],
				firstTimeGifts: [],
				subsequentGifts: []
			};
		}
	}

	filterBudgetInformation(activities) {
		return activities.map(activity => {
			const filtered = { ...activity };
			
			if (filtered.rawData) {
				const {
					costInCurrency,
					remainingBudget,
					budgetAfterTotal,
					budgetBeforeTotal,
					recognitionAmount,
					...safeRawData
				} = filtered.rawData;
				
				if (safeRawData.user) {
					const { recognitionEvents, ...safeUser } = safeRawData.user;
					safeRawData.user = safeUser;
				}
				
				filtered.rawData = safeRawData;
			}
			
			return filtered;
		});
	}

	processRecentActivities(badgeAssignments, snappyGifts) {
		const activities = [];
		
		badgeAssignments
			.filter(assignment => assignment.approvalStatus === 'Approved')
			.forEach(assignment => {
				const hasGift = assignment.badge?.snappyGrantId !== null;
				
				const recognitionActivity = {
					id: `badge-${assignment.id}`,
					type: 'badge_assignment',
					badgeName: assignment.badge?.name || 'Recognition',
					sentWithoutSnappyGrant: !hasGift,
					snappyGrantId: assignment.badge?.snappyGrantId,
					createdAt: assignment.updatedAt || assignment.createdAt,
					isNew: this.isNewActivity(assignment.updatedAt || assignment.createdAt),
					status: 'viewed',
					hasSeen: true,
					rawData: assignment
				};
				activities.push(recognitionActivity);
				
				if (hasGift && assignment.badge?.snappyGrant) {
					const giftActivity = {
						id: `gift-for-badge-${assignment.id}`,
						type: 'gift_received',
						badgeName: assignment.badge.name,
						snappyGrantId: assignment.badge.snappyGrantId,
						campaignTierId: assignment.badge.snappyGrant.id,
						createdAt: assignment.updatedAt || assignment.createdAt,
						isNew: this.isNewActivity(assignment.updatedAt || assignment.createdAt),
						status: 'pending',
						hasSeen: false,
						rawData: {
							...assignment,
							campaignTierId: assignment.badge.snappyGrant.id,
							giftName: assignment.badge.snappyGrant.name
						}
					};
					activities.push(giftActivity);
				}
			});
		activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
		
		return this.filterBudgetInformation(activities.slice(0, 10));
	}

	isNewActivity(timestamp) {
		const now = new Date();
		const activityTime = new Date(timestamp);
		const diffInHours = (now - activityTime) / (1000 * 60 * 60);
		return diffInHours <= 24;
	}
}

export default SnappyGiftService;